import React, { useContext, useEffect, useState } from 'react';
import { 
    AppBar, Box, Toolbar, IconButton, Menu, Typography, Container, 
    Button, Drawer, List, ListItem, ListItemButton, ListItemText, Divider, 
    MenuItem, Tooltip, Avatar, ListItemIcon, useTheme, Stack
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faSignOut, faUser, faGlobe, faRightToBracket, faIdCard } from '@fortawesome/free-solid-svg-icons';

// Assets & Context
import logo from '../../../assets/images/logos/logo.svg';
// import logo2 from '../../../assets/images/logos/logoQUDW.png'; // Úsalo si el fondo es oscuro al inicio
import ScrollToColor from './ScrollToColor';
import UserContext from '../../../navigation/context/userContext';
import AuthContext from '../../../navigation/context/authContext';
// import Searcher from '../../../components/searcher';

const HomeAppBar = (props) => {
    const { t, i18n } = useTranslation();
    const { userData } = useContext(UserContext);
    const { signOut } = useContext(AuthContext);
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();

    // Estados
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const [showSearcher, setShowSearcher] = useState(true);

    // Lógica de visualización del buscador (Mantenida de tu código)
    useEffect(() => {
        let path = location.pathname.split('/');
        if(['auth', 'transporte', 'ticketing', 'artemis'].includes(path[1]) || (path.length >= 6 && path[5] === 'menu')) {
            setShowSearcher(false);
        } else {
            setShowSearcher(true);
        }
    }, [location]);

    const handleLanguage = () => {
        i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
    }

    const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
    const handleCloseUserMenu = () => setAnchorElUser(null);
    
    const redirect = (route) => {
        history.push(route);
        setDrawerIsOpen(false);
    }

    const handleSignOut = () => {
        handleCloseUserMenu();
        history.replace('/');
        signOut();
    }

    // --- RENDERIZADO --- //

    return (
        <ScrollToColor>
            <AppBar position="fixed" color="transparent" elevation={0}>
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        
                        {/* 1. LOGO */}
                        <Box 
                            component="img"
                            src={logo}
                            alt="Anzoategui te enamora logo"
                            onClick={() => history.push('/')}
                            sx={{ 
                                height: { xs: 0, md: 50, lg:70 }, 
                                cursor: 'pointer',
                                transition: '0.3s',
                                '&:hover': { opacity: 0.8 }
                            }} 
                        />

                        {/* 2. BUSCADOR (Desktop) */}
                        <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1, px: 5, maxWidth: '600px' }}>
                            {/* showSearcher && <Searcher /> */}
                        </Box>

                        {/* 3. MENÚ DE ESCRITORIO (Desktop) */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                            
                            {/* Si NO está logueado (Invitado) */}
                            {userData.user === undefined ? (
                                <>
                                    <Button 
                                        onClick={() => redirect('/')}
                                        sx={{ color: 'text.primary', fontWeight: 600, textTransform: 'none' }}
                                    >
                                        {t('titles.home', 'Inicio')}
                                    </Button>

                                    <Button 
                                        startIcon={<FontAwesomeIcon icon={faGlobe} />}
                                        onClick={handleLanguage}
                                        sx={{ color: 'text.secondary', textTransform: 'none' }}
                                    >
                                        {i18n.language === 'es' ? 'ES' : 'EN'}
                                    </Button>

                                    <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: 'center' }} />

                                    <Button
                                        onClick={() => redirect('/auth/signIn')}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: '50px', // Estilo redondeado moderno
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            px: 3,
                                            borderColor: theme.palette.primary.main,
                                            '&:hover': {
                                                borderColor: theme.palette.primary.dark,
                                                backgroundColor: 'rgba(0,0,0,0.02)'
                                            }
                                        }}
                                    >
                                        {t('actions.signIn', 'Ingresar')}
                                    </Button>

                                    <Button
                                        onClick={() => redirect('/auth/userType')}
                                        variant="contained"
                                        disableElevation
                                        sx={{
                                            borderRadius: '50px',
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            px: 3,
                                            boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)' // Sombra suave color primary
                                        }}
                                    >
                                        Registrarse
                                    </Button>
                                </>
                            ) : (
                                /* Si ESTÁ logueado (Usuario) */
                                <Box>
                                    <Tooltip title="Mi Cuenta">
                                        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5, border: `2px solid ${theme.palette.divider}` }}>
                                            <Avatar sx={{ width: 35, height: 35, bgcolor: theme.palette.primary.main }}>
                                                {userData.user.name ? userData.user.name.charAt(0) : <FontAwesomeIcon icon={faUser} />}
                                            </Avatar>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        sx={{ mt: '45px' }}
                                        anchorEl={anchorElUser}
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        keepMounted
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                        PaperProps={{
                                            elevation: 3,
                                            sx: { borderRadius: 3, minWidth: 200 }
                                        }}
                                    >
                                        <Box sx={{ px: 2, py: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">Hola, {userData.user.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">Gestiona tu cuenta</Typography>
                                        </Box>
                                        <Divider />
                                        <MenuItem onClick={() => redirect('/')}>
                                            <ListItemIcon><FontAwesomeIcon icon={faHome} /></ListItemIcon>
                                            Inicio
                                        </MenuItem>
                                        <MenuItem onClick={handleSignOut}>
                                            <ListItemIcon><FontAwesomeIcon icon={faSignOut} /></ListItemIcon>
                                            Salir
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            )}
                        </Box>

                        {/* 4. MENÚ MÓVIL (Mobile Toggle) */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                            <IconButton 
                                onClick={() => setDrawerIsOpen(true)}
                                sx={{ color: 'text.primary' }}
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>
                        </Box>

                        {/* 5. DRAWER MÓVIL */}
                        <Drawer
                            anchor='right'
                            open={drawerIsOpen}
                            onClose={() => setDrawerIsOpen(false)}
                            PaperProps={{
                                sx: { width: '80%', maxWidth: 300, p: 2 }
                            }}
                        >
                            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                                <img src={logo} alt="QUD" style={{ height: 40 }} />
                            </Box>

                            <List>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => redirect('/')}>
                                        <ListItemIcon><FontAwesomeIcon icon={faHome}/></ListItemIcon>
                                        <ListItemText primary="Inicio" primaryTypographyProps={{fontWeight: 'bold'}} />
                                    </ListItemButton>
                                </ListItem>

                                {userData.user === undefined ? (
                                    <>
                                        <ListItem disablePadding>
                                            <ListItemButton onClick={() => redirect('/auth/signIn')}>
                                                <ListItemIcon><FontAwesomeIcon icon={faRightToBracket}/></ListItemIcon>
                                                <ListItemText primary="Iniciar Sesión" />
                                            </ListItemButton>
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemButton href='https://qud.life/auth/userType' component="a" target="_blank">
                                                <ListItemIcon><FontAwesomeIcon icon={faIdCard}/></ListItemIcon>
                                                <ListItemText primary="Registrarse" />
                                            </ListItemButton>
                                        </ListItem>
                                        <Divider sx={{ my: 2 }} />
                                        <ListItem disablePadding>
                                            <ListItemButton onClick={handleLanguage}>
                                                <ListItemIcon><FontAwesomeIcon icon={faGlobe}/></ListItemIcon>
                                                <ListItemText primary={i18n.language === 'es' ? 'Cambiar a Inglés' : 'Switch to Spanish'} />
                                            </ListItemButton>
                                        </ListItem>
                                    </>
                                ) : (
                                    <>
                                        <Divider sx={{ my: 1 }} />
                                        <ListItem disablePadding>
                                            <ListItemButton onClick={handleSignOut}>
                                                <ListItemIcon><FontAwesomeIcon icon={faSignOut} color="error"/></ListItemIcon>
                                                <ListItemText primary="Cerrar Sesión" primaryTypographyProps={{color: 'error.main'}} />
                                            </ListItemButton>
                                        </ListItem>
                                    </>
                                )}
                            </List>
                        </Drawer>

                    </Toolbar>
                </Container>
            </AppBar>
        </ScrollToColor>
    );
};
export default HomeAppBar;